import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { DETAIL_TABLE_WIDER_DATES_HINT } from '@/components/common/detailTableLayout.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { getInstituteHandOutStatCards } from '@/utils/handOutStatCards';
import { getTableEmptyText } from '@/utils/listSearch';
import { promoInstitutesQueryOptions, promoMembersQueryOptions } from '../api/promotion.api';
import { getPromoInstituteColumns } from '../components/PromoColumns';
import { PROMO_ADJUSTMENT_UNIT_DISPLAY } from '../constants/promotion.constants';
import { usePromoAdjustmentNavigation } from '../hooks/usePromoAdjustmentNavigation';
import type {
  PromoAdjustmentUnit,
  PromoDrillParams,
  PromoDrillSearchBox,
  PromoInstitute,
} from '../types/promotion.types';

const UNKNOWN_ZM_TITLE = 'ZM';

const toSearchableInstituteValues = (entry: PromoInstitute) => [
  entry.instituteName,
  entry.instituteCode === null ? null : String(entry.instituteCode),
  entry.instituteEiin,
  ...entry.meetPeople.flatMap((person) => [person.name, person.mobile]),
];

interface PromoItemZmInstitutesPageProps {
  unit: PromoAdjustmentUnit;
  promoItemId: number;
  zmId: number;
  zmSearch: PromoDrillParams;
  searchBox: PromoDrillSearchBox;
}

export function PromoItemZmInstitutesPage({
  unit,
  promoItemId,
  zmId,
  zmSearch,
  searchBox,
}: PromoItemZmInstitutesPageProps) {
  const { openItemZms } = usePromoAdjustmentNavigation(unit);
  useRememberViewedRow('promoItemZms', zmId, promoItemId);

  const unitDisplay = PROMO_ADJUSTMENT_UNIT_DISPLAY[unit];
  const zmsQuery = useQuery(promoMembersQueryOptions(promoItemId, zmSearch));
  const institutesQuery = useQuery(promoInstitutesQueryOptions(promoItemId, zmId, zmSearch));

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: institutesQuery.data,
    appliedText: searchBox.appliedText,
    onApply: searchBox.onApply,
    toSearchableValues: toSearchableInstituteValues,
  });

  const appliedSearchText = searchBox.appliedText;
  const columns = useMemo(
    () => getPromoInstituteColumns(unit, appliedSearchText),
    [unit, appliedSearchText],
  );

  const zm = zmsQuery.data?.find((member) => member.userId === zmId);
  const title = zmsQuery.isLoading ? undefined : (zm?.name ?? UNKNOWN_ZM_TITLE);

  return (
    <DetailTableLayout
      title={title}
      titleAddon={<DateRangeBadge dateFrom={zmSearch.dateFrom} dateTo={zmSearch.dateTo} />}
      search={{
        value: searchText,
        onChange: setSearchText,
        placeholder: 'Institute, code, EIIN or teacher',
      }}
      onNoHistory={() => openItemZms(promoItemId, zmSearch)}
      stats={getInstituteHandOutStatCards(institutesQuery.data, unitDisplay.amountUnit)}
      table={{
        rows: visibleRows,
        columns,
        isLoading: institutesQuery.isLoading,
        error: institutesQuery.error,
        errorTitle: 'Could not load the institutes',
        onRetry: () => void institutesQuery.refetch(),
        stickyColumnId: 'instituteName',
        ...getTableEmptyText(
          appliedSearchText,
          'No entry carries this item',
          DETAIL_TABLE_WIDER_DATES_HINT,
        ),
      }}
    />
  );
}
